import { useState } from "react";
import { X, MapPin, Calendar, Loader2, CheckCircle, Sparkles } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const RETREATS = {
  tulum: {
    label: "Roots Retreat — Tulum, Mexico",
    season: "Spring 2027",
    color: "#B89660",
  },
  bali: {
    label: "Awaken Retreat — Ubud, Bali",
    season: "Summer 2027",
    color: "#B89660",
  },
  brazil: {
    label: "Radiance Retreat — Chapada dos Guimarães, Brazil",
    season: "Fall 2027",
    color: "#B89660",
  },
  costarica: {
    label: "Rebirth Retreat — Nosara, Costa Rica",
    season: "Winter 2027",
    color: "#B89660",
  },
};

const RetreatWaitlistModal = ({ isOpen, onClose, defaultRetreat }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    retreat: defaultRetreat || "tulum",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Update retreat when defaultRetreat prop changes
  if (defaultRetreat && formData.retreat !== defaultRetreat && !isSubmitted) {
    setFormData((prev) => ({ ...prev, retreat: defaultRetreat }));
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/retreats/waitlist`, formData);
      setIsSubmitted(true);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSubmitted(false);
    setFormData({ name: "", email: "", retreat: "tulum", message: "" });
    onClose();
  };

  if (!isOpen) return null;

  const selected = RETREATS[formData.retreat];

  return (
    <div className="modal-backdrop" onClick={handleClose} data-testid="retreat-waitlist-modal">
      <div
        className="bg-[#F6F4EF] rounded-3xl max-w-xl w-full max-h-[92vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#F6F4EF] border-b border-[#E3DAC9] p-6 rounded-t-3xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#B89660]/15 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#B89660]" strokeWidth={1.5} />
            </div>
            <div>
              <span className="overline text-xs">Retreat Waitlist</span>
              <h2 className="font-heading text-xl text-[#1F2818]">
                Get on the list
              </h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-[#EAE2D0] rounded-full transition-colors"
            data-testid="retreat-waitlist-close"
          >
            <X className="w-5 h-5 text-[#4A5543]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <p className="text-[#4A5543] font-light mb-4 leading-relaxed">
                Small groups. Extraordinary places. Real transformation.
                Join the waitlist and I'll notify you first when spots open.
              </p>

              {/* Selected retreat preview */}
              <div className="rounded-2xl bg-white border border-[#E3DAC9] p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#B89660]/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-[#B89660]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#1F2818] font-medium text-sm leading-tight">
                    {selected.label}
                  </p>
                  <p className="text-[#4A5543] text-xs mt-0.5 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {selected.season}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#4A5543] mb-2">Which retreat calls you?</label>
                <select
                  name="retreat"
                  value={formData.retreat}
                  onChange={handleChange}
                  className="input-field"
                  data-testid="retreat-select"
                >
                  {Object.entries(RETREATS).map(([key, r]) => (
                    <option key={key} value={key}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-[#4A5543] mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter your full name"
                  required
                  data-testid="retreat-name-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#4A5543] mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter your email"
                  required
                  data-testid="retreat-email-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#4A5543] mb-2">
                  What are you hoping for? (optional)
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="input-field min-h-[90px] resize-none"
                  placeholder="Share what's calling you to this retreat..."
                  data-testid="retreat-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full"
                data-testid="retreat-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Adding you...
                  </>
                ) : (
                  "Join the Waitlist"
                )}
              </button>
            </form>
          ) : (
            <div className="text-center py-8 animate-scale-in">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-heading text-2xl text-[#1F2818] mb-3">
                You're on the list, {formData.name.split(" ")[0]}
              </h3>
              <p className="text-[#4A5543] mb-6 leading-relaxed">
                I'll notify you first when spots open for{" "}
                <strong className="text-[#1F2818]">{selected.label}</strong>.
                Watch your inbox for a warm welcome.
              </p>
              <button onClick={handleClose} className="btn-primary" data-testid="retreat-close-success-btn">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetreatWaitlistModal;
